import { ComponentProps } from 'preact';
import { ReactNode } from 'preact/compat';

import { cn } from '@/lib/utils';
import { HabitatListItem } from '@/queries/habitats/types';

import HabitatLink, { HabitatLinkProps } from '../HabitatLink';

interface HabitatGridItemProps extends ComponentProps<'div'> {
  habitat: HabitatListItem;
  renderLink?: (props: HabitatLinkProps) => ReactNode;
}

const HabitatGridItem = ({ habitat, className, renderLink, ...props }: HabitatGridItemProps) => {
  return (
    <div
      className={cn('flex flex-col rounded-2xl bg-white shadow-[0px_4px_12px_0px_rgba(0,0,0,0.20)]', className)}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
    >
      <div className="relative h-[142px] rounded-t-2xl bg-gray-400">
        {habitat.backgroundImage && (
          <img
            src={habitat.backgroundImage}
            alt={habitat.title}
            className="preflight preflight-img size-full overflow-hidden rounded-t-2xl object-cover"
          />
        )}
        <span
          data-online={habitat.isStreamOn}
          className="absolute right-3 top-2 flex rounded-md bg-offline-grey px-2 py-1 text-[10px]/none font-bold text-white data-[online=true]:bg-olive-3 data-[online=true]:text-hunter-green-3"
        >
          {habitat.isStreamOn ? 'Online' : 'Asleep'}
        </span>
        <div className="absolute inset-x-0 -bottom-7 flex items-center justify-center">
          {habitat.profileImage ? (
            <img
              src={habitat.profileImage}
              alt={habitat.title}
              className="preflight preflight-img size-[108px] overflow-hidden rounded-full border border-white object-cover"
            />
          ) : (
            <div className="preflight flex size-[108px] items-center justify-center overflow-hidden rounded-full border border-white bg-gray-400 text-2xl font-bold text-white">
              {habitat.title.charAt(0)}
            </div>
          )}
        </div>
      </div>
      <div className="grow px-4 pt-7 desktop:pt-9">
        <h3 className="preflight preflight-h3 mb-1 text-base font-bold text-neutral-950">{habitat.title}</h3>
        <p className="preflight preflight-p line-clamp-3 text-xs text-zinc-500">{habitat.description}</p>
      </div>
      <div className="px-4 pb-6 pt-3">
        {renderLink ? (
          renderLink({
            habitatId: habitat._id,
            slug: habitat.slug,
            zooSlug: habitat.zoo.slug,
            className:
              'preflight preflight-a block w-full rounded-full bg-primary-green px-4 py-2 text-center text-sm font-bold text-white hover:bg-secondary-green desktop:text-base',
            children: 'Visit Habitat',
          })
        ) : (
          <HabitatLink
            habitatId={habitat._id}
            slug={habitat.slug}
            zooSlug={habitat.zoo.slug}
            className="preflight preflight-a block w-full rounded-full bg-primary-green px-4 py-2 text-center text-sm font-bold text-white hover:bg-secondary-green desktop:text-base"
          >
            Visit Habitat
          </HabitatLink>
        )}
      </div>
    </div>
  );
};

export default HabitatGridItem;
